/* ============================================================
   hv-qty-net.js (2026-09-29)
   The quantity-limit and cart-error net, moved out of custom.js so the collection
   card limit chip could read each product's own limit (custom.quantity_max) instead
   of the hardcoded HV_LIMITS list. layout/theme.liquid loads this file with defer
   IMMEDIATELY BEFORE custom.js; it sets window.__hvQtyNetV2, so the older copy of
   this block still inside custom.js returns at its first line and never runs.
   EDIT THE LIMIT LOGIC HERE, NOT IN custom.js. If this file is ever removed from
   theme.liquid, custom.js's old copy (hardcoded list) takes over again.
   ============================================================ */
/* ============================================================
   Hobbiesville — Quantity-limit & cart-error net v4
   (toast fallback + clamp + Phase B violation UX + theme error
   cleanup + cart recovery)
   v4 (2026-07-07, Noibu issue #51): toast is now the single message
   surface for ALL /cart 422s (sold-out and inventory rejections
   included, using Shopify's own wording); the theme's popover-error is
   hidden for every cart error, not just limit ones; the LIMIT badge
   only flashes for genuine limit rejections; toast radius 4px -> 7px
   to match the Phase B badge/stepper family.
   v4.1 (same day): cart:add-item dispatch guard added — swallows the
   theme's cart:add-item on pages without [data-cart-toggle], where its
   unguarded listener (this.cartToggle.classList) crashes and the icon
   animation has no target anyway. Runtime substitute for patching
   theme.dev.js; remove when a guarded theme.dev.js (or v7) ships.
   v4.2 (same day): sold-out 422s — toast copy softened to "is now
   sold out" (race-honest wording) and the triggering form's Add to
   Cart button flips to the theme's real disabled Sold Out state
   ([data-add-to-cart][disabled] + theme.translations.sold_out), so a
   stale page stops inviting doomed clicks. Theme-owned add forms only;
   StorePass React buttons stay toast-only until Trent's native build.
   Pairs with the "max-per-order" checkout validation
   (Hobbiesville Ops; custom.quantity_max / quantity_min) and the
   Phase B Liquid (limit badges, [data-hv-limit-cap] inputs,
   [data-hv-limit-alert] box, .hv-limit-note cart notes).
   Phase B handlers are inert on themes without those attributes.
   Removable any time: delete this block.
   ============================================================ */
(function () {
  'use strict';
  if (window.__hvQtyNetV2) return; // guard against double-paste
  window.__hvQtyNetV2 = true;

  var CART_MUTATION = /\/cart\/(add|change|update)/;
  var LIMIT_MSG = /(Limit \d+ per customer|Minimum \d+ per order|Only \d+ per customer)/;
  var lastText = '', lastAt = 0, lastQtyEl = null;

  /* --- 2026-09-29: PER-CUSTOMER LIMITS FOR STOREPASS CARDS, READ FROM THE PRODUCT.
         Replaces the hardcoded HV_LIMITS map (a 2026-07-07 snapshot of 22 handles that
         had drifted: most limited products had no chip, and two unlimited ones had one).
         The source of truth is now the same field the PDP and cart use,
         product.metafields.custom.quantity_max, fetched per product through the
         section rendering API: /products/<handle>?section_id=hv-limit-data returns
         <span data-hv-limit="N" data-hv-limit-label="..."> (the label is the theme's
         translated products.form.limit_per_customer, same as the PDP badge; the
         value is empty when the product has no limit, below 1 = off,
         same rule as cart-line-items.liquid). Only handles that sit on a real card
         (a product link plus a number input) are looked up, at most 4 at a time.
         Answers are cached per handle for 10 minutes in sessionStorage so paging and
         back navigation are instant. A failed lookup is treated as "no limit" for this
         page view and retried on the next one; checkout validation stays the wall. --- */
  var HV_LIMIT_TTL = 10 * 60 * 1000;
  var HV_LIMIT_KEY = 'hvLimits:v2';
  var HV_LIMIT_CACHE = {};   /* handle -> [limit (0 = none), fetchedAt, label] */
  var HV_LIMIT_WANT = [];    /* handles waiting for a lookup */
  var HV_LIMIT_BUSY = {};    /* handle -> true while queued or in flight */
  var HV_LIMIT_ACTIVE = 0;
  try {
    var hvStored = JSON.parse(window.sessionStorage.getItem(HV_LIMIT_KEY) || '{}');
    var hvNow = Date.now();
    for (var hvH in hvStored) {
      if (hvStored.hasOwnProperty(hvH) && hvStored[hvH] && hvNow - hvStored[hvH][1] < HV_LIMIT_TTL) HV_LIMIT_CACHE[hvH] = hvStored[hvH];
    }
  } catch (e) { /* storage blocked: lookups simply are not remembered */ }
  function hvSaveLimits() {
    try { window.sessionStorage.setItem(HV_LIMIT_KEY, JSON.stringify(HV_LIMIT_CACHE)); } catch (e) {}
  }
  /* undefined = not known yet, 0 = no limit, N = limit N per customer */
  function hvLimitOf(handle) {
    var hit = HV_LIMIT_CACHE[handle];
    if (hit && Date.now() - hit[1] < HV_LIMIT_TTL) return hit[0];
    return undefined;
  }
  /* the theme's translated wording (same key as the PDP badge); English fallback */
  function hvLimitLabel(handle, lim) {
    var hit = HV_LIMIT_CACHE[handle];
    return (hit && hit[2]) || ('Limit ' + lim + ' per customer');
  }
  function hvRequestLimit(handle) {
    if (HV_LIMIT_BUSY[handle]) return;
    HV_LIMIT_BUSY[handle] = true;
    HV_LIMIT_WANT.push(handle);
    hvPumpLimits();
  }
  function hvPumpLimits() {
    while (HV_LIMIT_ACTIVE < 4 && HV_LIMIT_WANT.length) {
      (function (handle) {
        HV_LIMIT_ACTIVE++;
        var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        fetch(root + 'products/' + encodeURIComponent(handle) + '?section_id=hv-limit-data', { credentials: 'same-origin' })
          .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
          .then(function (html) {
            var el = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-hv-limit]');
            var n = el ? parseInt(el.getAttribute('data-hv-limit'), 10) : 0;
            var label = el ? (el.getAttribute('data-hv-limit-label') || '') : '';
            HV_LIMIT_CACHE[handle] = [n > 0 ? n : 0, Date.now(), label];
            hvSaveLimits();
          })
          .catch(function () {
            /* not cached: "no limit" for this page view only */
            HV_LIMIT_CACHE[handle] = [0, Date.now() - HV_LIMIT_TTL + 1];
          })
          .then(function () {
            HV_LIMIT_ACTIVE--;
            if (!HV_LIMIT_WANT.length && !HV_LIMIT_ACTIVE) {
              /* everything on screen is known now: paint the chips (bypass the throttle) */
              hvDecorateT = 0;
              hvDecorateCards();
            }
            hvPumpLimits();
          });
      })(HV_LIMIT_WANT.shift());
    }
  }

  /* --- styles: toast + shake animation --- */
  var css = document.createElement('style');
  css.textContent =
    '@keyframes hvShake{10%,90%{transform:translateX(-1px)}20%,80%{transform:translateX(2px)}' +
    '30%,50%,70%{transform:translateX(-3px)}40%,60%{transform:translateX(3px)}}' +
    '.hv-limit-shake{animation:hvShake .5s ease both;box-shadow:0 0 0 2px var(---color-error-text,#d02e2e)!important;' +
    'border-radius:7px;transition:box-shadow .3s ease;}' +
    '.hv-limit-violate{animation:hvShake .5s ease both;border-color:var(---color-error-border,#d02e2e)!important;' +
    'color:var(---color-error-text,#d02e2e)!important;transition:border-color .3s ease,color .3s ease;}' +
    '.hv-limit-chip-wrap{width:100%;display:flex;justify-content:center;margin:0 0 5px;}' +
    '.hv-limit-chip{display:inline-flex;align-items:center;margin:0;padding:4px 10px;' +
    'border:1px solid var(---color-border,#000);border-radius:7px;color:var(---color-text,#000);' +
    'font-size:12px;line-height:1.3;font-weight:var(---body-font-weight-bold,700);' +
    'letter-spacing:.04em;text-transform:uppercase;white-space:nowrap;}' +
    /* 2026-09-29: never wider than its card. At 320 px a grid card is 149 px and the
       chip was 173 px (12 px past each edge): smaller type below 360 px, and wrap
       as a last resort instead of overflowing. */
    '.hv-limit-chip{max-width:100%;box-sizing:border-box;justify-content:center;text-align:center;}' +
    '@media (max-width: 359px){.hv-limit-chip{font-size:10px;letter-spacing:0;padding:4px 6px;white-space:normal;}}' +
    '@media (prefers-reduced-motion: reduce){.hv-limit-shake,.hv-limit-violate{animation:none;}}';
  document.head.appendChild(css);

  function toastEl() {
    var el = document.getElementById('hv-qty-toast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'hv-qty-toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      el.style.cssText =
        'position:fixed;left:50%;bottom:20px;transform:translateX(-50%);' +
        'max-width:92vw;box-sizing:border-box;padding:10px 20px;' +
        'background:#000;color:#fff;border-radius:7px;' +
        'font-size:14px;line-height:1.45;text-align:center;white-space:pre-line;' +
        'box-shadow:0 4px 16px rgba(0,0,0,.35);z-index:2147483000;' +
        'opacity:0;transition:opacity .25s ease;pointer-events:none;';
      (document.body || document.documentElement).appendChild(el);
    }
    return el;
  }

  function showToast(text) {
    var now = Date.now();
    if (text === lastText && now - lastAt < 1500) return;
    lastText = text; lastAt = now;
    var el = toastEl();
    el.textContent = text;
    el.style.opacity = '1';
    clearTimeout(el.__hvT);
    el.__hvT = setTimeout(function () { el.style.opacity = '0'; }, 4000);
  }

  /* ============ Phase B violation UX ============
     The limit badge is the violation surface — PDP badge above the stepper,
     or the cart line's limit chip — it shakes and flashes error-red while
     the stepper keeps its red ring + shake. The theme's misleading
     "No more inventory available" tooltip is stripped for limit caps. */

  function hvHolder(i) { return i.closest('[data-quantity-holder]'); }

  function hvShowViolation(input) {
    if (!input || !input.hasAttribute || !input.hasAttribute('data-hv-limit-cap')) return false;
    var h = hvHolder(input), hit = false;
    /* the limit badge is the violation surface: PDP badge above the stepper,
       or the cart line's limit note — it shakes and flashes error-red */
    var chip = null;
    var form = input.closest('form');
    if (form) chip = form.querySelector('[data-hv-limit-badge]');
    if (!chip) {
      var cell = input.closest('.cart-product__quantity');
      chip = cell && cell.querySelector('.hv-limit-note');
    }
    if (!chip) {
      var card = input.closest('[data-hv-limit-card]');
      chip = card && card.querySelector('.hv-limit-chip');
    }
    /* 2026-09-07: PDP buy box. Its quantity input is NOT a DOM descendant of a <form>
       — it is bound by the form="product-form-…" attribute — so the closest('form')
       lookup above returns null and never finds the Liquid-rendered badge. Measured:
       the shake fired but the badge never flashed.

       [data-hv-limit-scope] is emitted by snippets/buy-buttons.liquid on the wrapper
       holding both the badge and the stepper. It is deliberately a SEPARATE hook from
       data-hv-limit-card: that one opts an element into hvDecorateCards, which would
       re-derive the input's cap from its own card lookup (see the guard in
       hvDecorateCards). Liquid-owned surfaces must keep
       the metafield as their source of truth. */
    if (!chip) {
      var scope = input.closest('[data-hv-limit-scope]');
      chip = scope && scope.querySelector('[data-hv-limit-badge]');
    }
    if (chip) {
      chip.classList.remove('hv-limit-violate');
      void chip.offsetWidth;
      chip.classList.add('hv-limit-violate');
      clearTimeout(chip.__hvT);
      chip.__hvT = setTimeout(function () { chip.classList.remove('hv-limit-violate'); }, 4000);
      hit = true;
    }
    if (h) {
      h.classList.remove('hv-limit-shake');
      void h.offsetWidth;
      h.classList.add('hv-limit-shake');
      setTimeout(function () { h.classList.remove('hv-limit-shake'); }, 1300);
      var plus = h.querySelector('[data-quantity-plus]');
      if (plus) setTimeout(function () {
        plus.removeAttribute('data-quantity-tooltip');
        plus.removeAttribute('data-quantity-tooltip-visible');
        plus.removeAttribute('title');
      }, 0);
    }
    return hit;
  }

  /* + pressed at the wall: value can't change, so catch the click itself.
     Swallow the event entirely (capture phase runs before the theme's
     handlers) — otherwise the theme fires a same-quantity /cart/change.js,
     shows its loading state, then its limit banner: shake→load→shake.
     One clean violation moment instead. */
  function hvTarget(ev) {
    /* shadow DOM retargets events at document level — composedPath keeps
       the real origin (StorePass search renders in a shadow tree) */
    return (ev.composedPath && ev.composedPath()[0]) || ev.target;
  }

  document.addEventListener('click', function (ev) {
    var tgt = hvTarget(ev);
    var plus = tgt && tgt.closest && tgt.closest('[data-quantity-plus]');
    if (plus) {
      var h = plus.closest('[data-quantity-holder]');
      var capped = h && h.querySelector('input[data-hv-limit-cap]');
      if (capped) {
        var mx = parseInt(capped.getAttribute('max'), 10);
        var vl = parseInt(capped.value, 10);
        if (!isNaN(mx) && !isNaN(vl) && vl >= mx) {
          ev.preventDefault();
          ev.stopPropagation();
          hvShowViolation(capped);
          return;
        }
      }
    }
    /* remember the stepper the shopper last touched (toast/shake targeting) */
    var b = tgt && tgt.closest && tgt.closest('button, [data-quantity-plus], [data-quantity-minus]');
    if (!b) return;
    var wrap = b.closest('[data-quantity-holder], .product__quantity, .cart__quantity, form, td, div');
    var input = wrap && wrap.querySelector && wrap.querySelector('input[name="quantity"], input[name="updates[]"], input[type="number"]');
    if (input) lastQtyEl = input;
  }, true);

  /* typed overflow: remember the raw value before the clamp rewrites it */
  document.addEventListener('input', function (ev) {
    var t = hvTarget(ev);
    if (t && t.tagName === 'INPUT' && t.hasAttribute('data-hv-limit-cap')) t.__hvRaw = parseInt(t.value, 10);
  }, true);
  document.addEventListener('change', function (ev) {
    var t = hvTarget(ev);
    if (t && t.tagName === 'INPUT' &&
        (t.type === 'number' || t.name === 'quantity' || t.name === 'updates[]')) lastQtyEl = t;
  }, true);

  function shakeQty() {
    var el = lastQtyEl ||
      document.querySelector('form[action*="/cart/add"] input[name="quantity"]');
    if (!el) return;
    var target = el.closest('[data-quantity-holder], .product__quantity') || el;
    target.classList.remove('hv-limit-shake');
    void target.offsetWidth; // restart animation
    target.classList.add('hv-limit-shake');
    setTimeout(function () { target.classList.remove('hv-limit-shake'); }, 1300);
  }

  /* --- TEMPORARY: decorate StorePass-rendered product cards (collection/search
         grids) with a LIMIT chip and tag their inputs, so limited products are
         visible before rejection and hvShowViolation has a surface to flash.
         React owns those steppers, so no hard cap here — the checkout validation
         is the wall. Self-healing via the MutationObserver (re-inserts after
         React re-renders). Limits come from hvLimitOf() (product metafield). --- */
  var hvDecorateT = 0;
  function hvDeepLinks() {
    /* product links in the document AND inside any open shadow roots
       (StorePass search results render in a shadow tree) */
    var out = [];
    function collect(root) {
      var found = root.querySelectorAll('a[href*="/products/"]');
      for (var i = 0; i < found.length; i++) out.push(found[i]);
    }
    collect(document);
    try {
      var walker = document.createTreeWalker(document.documentElement, NodeFilter.SHOW_ELEMENT);
      var n;
      while ((n = walker.nextNode())) {
        if (n.shadowRoot) collect(n.shadowRoot);
      }
    } catch (e) { /* older browsers: document-level links already collected */ }
    return out;
  }
  /* 2026-09-04: StorePass's own rule is "price $0 -> Contact Us" regardless of
     stock, and it still paints the OUT OF STOCK pill. The store rule is
     "$0 AND Warehouse stock -> Contact Us; $0 without -> Notify Me" (the PDP
     does this in Liquid). Align the grid: a Contact Us card that carries the
     out-of-stock pill becomes a grey Notify Me (pill stays); one without the
     pill keeps Contact Us and loses the $0.00 price. Re-runs on every StorePass
     re-render via the existing decorator observer. Spec for Trent to own this
     server-side is in the StorePass notes. */
  function hvContactCards() {
    var ctas = document.querySelectorAll('.store-pass-product-cta, button, a');
    for (var i = 0; i < ctas.length; i++) {
      var c = ctas[i];
      if (c.hasAttribute('data-hv-contact-card')) continue;
      var label = (c.textContent || '').trim().toLowerCase();
      if (label !== 'contact us') continue;
      /* 2026-09-09: STOREPASS CARDS ONLY. Our own rails render a Contact Us
         button too, so they matched this scan, and the [class*="price"] sweep
         below then set display:none on .hv-rail__price-container — collapsing
         the card by one line and lifting its stepper and CTA above every other
         card in the rail (measured on /cart: CTA top 1637 vs 1662). Our cards
         compute st.contact themselves and reserve the row, so they need none of
         this repair. Skip anything inside one of ours. */
      if (c.closest && c.closest('.hv-rail__card, [data-hv-rail], .hv-continue, .hv-cp, [data-hv-bd-side], [data-hv-bd-item]')) continue;
      var card = c, d = 0;
      while (card && d < 8 && !(card.querySelector && card.querySelector('a[href*="/products/"]') && card.querySelector('img'))) { card = card.parentElement; d++; }
      c.setAttribute('data-hv-contact-card', '1');
      if (!card) continue;
      /* rule: $0 = Contact Us regardless of stock -> the OUT OF STOCK pill is wrong here */
      var badges = card.querySelectorAll('.store-pass-badge, [class*="badge"]');
      for (var b = 0; b < badges.length; b++) {
        if (/out of stock|sold out/i.test(badges[b].textContent || '')) badges[b].style.display = 'none';
      }
      var prices = card.querySelectorAll('[class*="price"]');
      for (var p = 0; p < prices.length; p++) {
        if (/^\$?0([.,]00)?$/.test((prices[p].textContent || '').replace(/[\s]|CAD|USD/g, ''))) prices[p].style.display = 'none';
      }
    }
  }
  function hvDecorateCards() {
    var now = Date.now();
    if (now - hvDecorateT < 300) return;
    hvDecorateT = now;
    try { hvContactCards(); } catch (e) {}
    var links = hvDeepLinks();
    for (var i = 0; i < links.length; i++) {
      var m = /\/products\/([a-z0-9\-]+)/.exec(links[i].getAttribute('href') || '');
      if (!m) continue;
      var lim = hvLimitOf(m[1]);
      if (lim === 0) continue; /* known: no limit */
      if (links[i].__hvNoCard) continue; /* menu, breadcrumb, etc.: checked once already */
      /* climb to the card: nearest ancestor that also contains a number input */
      var node = links[i], card = null, depth = 0;
      while (node && depth < 7) {
        node = node.parentElement; depth++;
        if (node && node.querySelector && node.querySelector('input[type="number"]')) { card = node; break; }
      }
      if (!card) { links[i].__hvNoCard = true; continue; }
      /* grid-level false positive guard: a card links to exactly one product */
      var hrefs = {};
      var cardLinks = card.querySelectorAll('a[href*="/products/"]');
      for (var j = 0; j < cardLinks.length; j++) {
        var mm = /\/products\/([a-z0-9\-]+)/.exec(cardLinks[j].getAttribute('href') || '');
        if (mm) hrefs[mm[1]] = true;
      }
      if (Object.keys(hrefs).length !== 1) continue;
      var input = card.querySelector('input[type="number"]');
      if (!input) continue;
      /* Liquid-owned surfaces (PDP buy box, cart lines) render their own
         badge/note below or above the stepper — never double-chip them.
         Decorator-processed cards carry data-hv-limit-card and stay
         eligible so the chip self-heals after React re-renders. */
      if (input.hasAttribute('data-hv-limit-cap') && !card.hasAttribute('data-hv-limit-card')) continue;
      /* a real card whose limit is not known yet: look it up, paint when it lands */
      if (lim === undefined) { hvRequestLimit(m[1]); continue; }
      card.setAttribute('data-hv-limit-card', m[1]);
      input.setAttribute('data-hv-limit-cap', lim);
      /* our own rails (PDP related products, cart "Complete your cart") build their
         stepper from input[max], so the + stops at the limit like the PDP. StorePass
         cards are React-owned and are left to checkout validation. */
      if (card.classList && card.classList.contains('hv-rail__card')) {
        var curMax = parseInt(input.getAttribute('max'), 10);
        if (isNaN(curMax) || curMax > lim) input.setAttribute('max', lim);
        if ((parseInt(input.value, 10) || 1) > lim) input.value = lim;
      }
      if (!card.querySelector('.hv-limit-chip')) {
        var wrap = document.createElement('div');
        wrap.className = 'hv-limit-chip-wrap';
        var chipEl = document.createElement('span');
        chipEl.className = 'hv-limit-chip';
        chipEl.textContent = hvLimitLabel(m[1], lim);
        wrap.appendChild(chipEl);
        var row = input.parentElement || input;
        if (row.parentElement) row.parentElement.insertBefore(wrap, row);
      }
    }
  }
  hvDecorateCards();

  /* --- theme error cleanup: duplicated pink popover (PDP) and the
         "No more products available for purchase" cart banner, which is
         inaccurate for per-customer caps — the line chip owns that message --- */
  var mo = new MutationObserver(function () {
    hvDecorateCards();
    document.querySelectorAll('.popover-error').forEach(function (p) {
      /* v4: the toast owns cart-error messaging on every surface — the
         header popover is invisible on mobile (~4 in 5 of these errors)
         and would double-message on desktop, so hide it for every cart
         error. Targets the transient .popover-error node itself, so the
         container still shows later success popovers. */
      var holder = p.closest('[class*="popover"]') || p;
      holder.style.display = 'none';
    });
    document.querySelectorAll('[data-cart-errors]').forEach(function (banner) {
      var msg = banner.querySelector('[data-error-message]');
      var title = msg && msg.textContent.trim();
      if (!title) return;
      var inputs = document.querySelectorAll('input[data-hv-limit-cap]');
      for (var k = 0; k < inputs.length; k++) {
        var row = inputs[k].closest('[data-item]');
        var t = row && row.querySelector('.cart-product__title');
        if (t && t.textContent.trim() === title) {
          banner.style.display = 'none';
          msg.textContent = '';
          hvShowViolation(inputs[k]);
          return;
        }
      }
    });
  });
  mo.observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] });

  function collectMessages(j) {
    var out = [];
    function push(v) {
      if (typeof v === 'string' && v.trim() && out.indexOf(v.trim()) === -1) out.push(v.trim());
    }
    if (!j || typeof j !== 'object') return out;
    push(j.description);
    if (j.message && j.message !== 'Cart Error') push(j.message);
    var e = j.errors;
    if (typeof e === 'string') push(e);
    else if (Array.isArray(e)) e.forEach(push);
    else if (e && typeof e === 'object') {
      Object.keys(e).forEach(function (k) {
        var v = e[k];
        if (Array.isArray(v)) { v.forEach(push); } else { push(v); }
      });
    }
    return out;
  }

  var SOLD_OUT_MSG = /is already sold out/;

  /* sold-out 422: the page just proved itself stale — flip the add
     button that produced the click into the theme's genuine Sold Out
     state (same attributes Liquid renders server-side) so it stops
     inviting more clicks. Conservative scope: only the add-to-cart
     form the shopper touched, else the main PDP form. React-owned
     (StorePass) buttons are left alone — React would clobber the
     mutation on re-render; their fix lands with the native build. */
  function hvMarkSoldOut() {
    try {
      var scope = (lastQtyEl && lastQtyEl.closest && lastQtyEl.closest('form[action*="/cart/add"]')) ||
        (/^\/products\//.test(location.pathname) && document.querySelector('form[action*="/cart/add"]'));
      if (!scope) return;
      var btn = scope.querySelector('[data-add-to-cart]') || scope.querySelector('button[name="add"]');
      if (!btn) return;
      btn.setAttribute('disabled', 'disabled');
      var label = (window.theme && theme.translations && theme.translations.sold_out) || 'Sold Out';
      var txt = btn.querySelector('[data-add-to-cart-text]') || btn;
      txt.textContent = label;
    } catch (e) { /* cosmetic only — never break the toast */ }
  }

  function handleBlocked(json, kind) {
    try {
      var msgs = collectMessages(json);
      var isLimit = LIMIT_MSG.test(msgs.join('\n'));
      if (SOLD_OUT_MSG.test(msgs.join('\n'))) {
        msgs = msgs.map(function (m) { return m.replace(/is already sold out/g, 'is now sold out'); });
        hvMarkSoldOut();
      }
      /* v4: route by rejection type. Limit rejections -> the Phase B
         badge/chip is the violation surface (toast only if that surface is
         missing). Inventory rejections (sold out / all remaining stock
         already in cart) -> toast with Shopify's own wording; never flash
         the LIMIT badge for a stock problem. */
      if (!(isLimit && hvShowViolation(lastQtyEl))) {
        showToast(msgs.length ? msgs.slice(0, 3).join('\n')
                              : 'Couldn’t add to cart — item unavailable.');
        shakeQty();
      }
      // Cart page: the theme's change handler has no error path and leaves
      // the row spinning — resync after the shopper has seen the message.
      if (kind !== 'add' && /^\/cart\/?$/.test(location.pathname)) {
        setTimeout(function () { location.reload(); }, 1600);
      }
    } catch (e) { /* never break the page */ }
  }

  function kindFromUrl(url) {
    var m = /\/cart\/(add|change|update)/.exec(url || '');
    return m ? m[1] : '';
  }

  /* --- fetch interceptor (theme + StorePass) --- */
  if (window.fetch) {
    var hvFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = '', method = 'GET';
      try {
        url = (input && input.url) ? input.url : String(input || '');
        method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
      } catch (e) {}
      var p = hvFetch.apply(this, arguments);
      if (method === 'POST' && CART_MUTATION.test(url)) {
        p.then(function (res) {
          if (res && res.status === 422) {
            res.clone().json().then(function (j) { handleBlocked(j, kindFromUrl(url)); })
              .catch(function () { handleBlocked(null, kindFromUrl(url)); });
          }
        }).catch(function () {});
      }
      return p;
    };
  }

  /* --- XHR interceptor (jQuery $.ajax cart calls) --- */
  if (window.XMLHttpRequest) {
    var hvOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      try {
        this.__hvCartKind = (String(method || '').toUpperCase() === 'POST')
          ? kindFromUrl(String(url || '')) : '';
      } catch (e) { this.__hvCartKind = ''; }
      return hvOpen.apply(this, arguments);
    };
    var hvSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function () {
      if (this.__hvCartKind) {
        var kind = this.__hvCartKind;
        this.addEventListener('load', function () {
          if (this.status === 422) {
            var j = null;
            try { j = JSON.parse(this.responseText); } catch (e) {}
            handleBlocked(j, kind);
          }
        });
      }
      return hvSend.apply(this, arguments);
    };
  }

  /* --- cart:add-item dispatch guard: the theme's listener does
         this.cartToggle.classList.add(...) unguarded and crashes on pages
         without [data-cart-toggle] (Noibu: “null is not an object
         (evaluating this.carttoggle.classlist)”, co-timed with every add
         click). The listener only animates that toggle, so on toggle-less
         pages the event has no work to do — swallow it there. Same
         interceptor pattern as the fetch/XHR nets above. Element-level
         dispatches are untouched (override sits on document only). --- */
  var hvDispatch = document.dispatchEvent.bind(document);
  document.dispatchEvent = function (ev) {
    try {
      if (ev && ev.type === 'cart:add-item' && !document.querySelector('[data-cart-toggle]')) {
        return true; /* uncancelled no-op; theme treats dispatch as fire-and-forget */
      }
    } catch (e) { /* fall through to real dispatch */ }
    return hvDispatch(ev);
  };

  /* --- stepper clamp: typed values above max snap back; capped inputs get
         the full violation moment, everything else gets the plain shake --- */
  document.addEventListener('change', function (ev) {
    var t = hvTarget(ev);
    if (!t || t.tagName !== 'INPUT') return;
    var isQty = t.type === 'number' || t.name === 'quantity' || t.name === 'updates[]';
    if (!isQty || !t.hasAttribute('max')) return;
    var max = parseInt(t.getAttribute('max'), 10);
    var min = parseInt(t.getAttribute('min'), 10);
    var val = parseInt(t.value, 10);
    var raw = t.__hvRaw; t.__hvRaw = null;
    if (isNaN(val)) return;
    if (!isNaN(max) && (val > max || (!isNaN(raw) && raw > max))) {
      if (val > max) t.value = max;
      if (!hvShowViolation(t)) shakeQty();
    }
    else if (!isNaN(min) && val < min) { t.value = min; }
  }, true);
})();
